import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Box, Card, CritPill, DefPill, EffPill, Empty, EvidPill, ExecPill, Head, MemberPick } from "@/components/controls/ui";
import { NONE, Pick } from "@/components/vendors/NewVendorDialog";
import { api } from "@/lib/api";
import { fmtD, fmtDT } from "@/lib/platform";
import { downloadControlEvidence, exportReport, hasPerm, lbl, opts, run } from "@/lib/controls";

const useList = (url, params) => {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => api.get(url, { params: JSON.parse(key) }).then(({ data }) => setD(data)).catch(() => {}), [url, key]);
  useEffect(() => { load(); }, [load]);
  return [d, load];
};

const VIEWS = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["quarter", "Quarter"], ["overdue", "Overdue"], ["", "All"]];
export const CalendarView = ({ meta }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ view: "month" });
  const [d, load] = useList("/control-executions", Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
  const gen = () => run(() => api.post("/control-executions/generate-due"), "Scheduler run complete").then(load);
  return (
    <div className="space-y-3" data-testid="control-calendar">
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map(([k, l]) => <Button key={k} size="sm" variant={f.view === k ? "default" : "outline"} onClick={() => setF((x) => ({ ...x, view: k }))} data-testid={`cal-view-${k || "all"}`}>{l}</Button>)}
        <div className="w-40"><Pick value={f.domain || NONE} none="All domains" options={opts(meta?.domains)} onChange={(v) => setF((x) => ({ ...x, domain: v === NONE ? "" : v }))} testId="cal-domain" /></div>
        <div className="w-36"><Pick value={f.criticality || NONE} none="All criticality" options={opts(meta?.criticality)} onChange={(v) => setF((x) => ({ ...x, criticality: v === NONE ? "" : v }))} testId="cal-criticality" /></div>
        <div className="w-44"><MemberPick meta={meta} value={f.owner} none="All owners" onChange={(v) => setF((x) => ({ ...x, owner: v || "" }))} testId="cal-owner" /></div>
        <div className="w-40"><Pick value={f.team || NONE} none="All teams" options={(meta?.teams || []).map((t) => [t.id, t.name])} onChange={(v) => setF((x) => ({ ...x, team: v === NONE ? "" : v }))} testId="cal-team" /></div>
        {hasPerm(meta?.my_permissions, "CONTROL_EDIT") && <Button size="sm" variant="outline" onClick={gen} data-testid="generate-due-btn">Run scheduler</Button>}
      </div>
      <Box testId="execution-table">
        <Head cols={["Execution", "Control", "Period", "Due", "Owner / operator", "Trigger", "Result", "Status"]} />
        <tbody>
          {!d?.executions?.length && <Empty cols={8} text="No executions in this view." />}
          {d?.executions?.map((e) => (
            <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" onClick={() => nav(`/controls/${e.control_id}?tab=executions&exec=${e.id}`)} data-testid={`exec-row-${e.execution_reference}`}>
              <td className="px-3 py-2.5 font-mono text-xs">{e.execution_reference}</td>
              <td className="px-3 py-2.5"><div className="font-mono text-[11px] text-slate-500">{e.control_ref}</div><div className="text-[#003366]">{e.control_name}</div></td>
              <td className="px-3 py-2.5 text-xs font-mono">{fmtD(e.execution_period_start)} - {fmtD(e.execution_period_end)}</td>
              <td className="px-3 py-2.5 text-xs font-mono">{fmtD(e.scheduled_for)}</td>
              <td className="px-3 py-2.5 text-xs">{e.owner_name}<div className="text-slate-400">{e.operator_name}</div></td>
              <td className="px-3 py-2.5 text-[11px]">{lbl(e.trigger_type)}{e.source_label && <div className="text-slate-400 truncate max-w-[160px]">{e.source_label}</div>}</td>
              <td className="px-3 py-2.5 text-[11px]">{lbl(e.result)}</td>
              <td className="px-3 py-2.5"><ExecPill s={e.execution_status} /></td>
            </tr>
          ))}
        </tbody>
      </Box>
    </div>
  );
};

export const TestsList = () => {
  const nav = useNavigate();
  const [d] = useList("/control-tests");
  return (
    <Box testId="tests-table">
      <Head cols={["Test", "Control", "Type", "Period", "Tester", "Design", "Operating", "Overall", "Status"]} />
      <tbody>
        {!d?.tests?.length && <Empty cols={9} text="No control tests yet." />}
        {d?.tests?.map((t) => (
          <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" onClick={() => nav(`/controls/tests/${t.id}`)} data-testid={`test-row-${t.test_ref}`}>
            <td className="px-3 py-2.5 font-mono text-xs">{t.test_ref}{t.prior_test_id && <div className="text-[10px] text-purple-600">retest</div>}</td>
            <td className="px-3 py-2.5"><span className="font-mono text-[11px] text-slate-500">{t.control_ref}</span> {t.control_name}</td>
            <td className="px-3 py-2.5 text-xs">{t.type_label}</td>
            <td className="px-3 py-2.5 text-xs font-mono">{fmtD(t.test_period_start)} - {fmtD(t.test_period_end)}</td>
            <td className="px-3 py-2.5 text-xs">{t.tester_name}</td>
            <td className="px-3 py-2.5"><EffPill s={t.design_effectiveness_result} /></td>
            <td className="px-3 py-2.5"><EffPill s={t.operating_effectiveness_result} /></td>
            <td className="px-3 py-2.5"><EffPill s={t.overall_test_result} /></td>
            <td className="px-3 py-2.5 text-xs">{t.status_label}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export const DeficiencyList = ({ controlId }) => {
  const nav = useNavigate();
  const [d] = useList("/deficiencies", controlId ? { control_id: controlId } : {});
  return (
    <Box testId="deficiency-table">
      <Head cols={["Deficiency", "Title", "Control", "Severity", "Owner", "Treatment", "Age", "Target", "Status"]} />
      <tbody>
        {!d?.deficiencies?.length && <Empty cols={9} text="No deficiencies." />}
        {d?.deficiencies?.map((x) => (
          <tr key={x.id} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" onClick={() => nav(`/controls/deficiencies/${x.id}`)} data-testid={`def-row-${x.deficiency_reference}`}>
            <td className="px-3 py-2.5 font-mono text-xs">{x.deficiency_reference}</td>
            <td className="px-3 py-2.5 text-[#003366]">{x.title}</td>
            <td className="px-3 py-2.5 font-mono text-[11px]">{x.control_ref}</td>
            <td className="px-3 py-2.5"><CritPill s={x.severity} /></td>
            <td className="px-3 py-2.5 text-xs">{x.owner_name}</td>
            <td className="px-3 py-2.5 text-xs">{x.treatment_label || "-"}</td>
            <td className="px-3 py-2.5 text-xs font-mono">{x.age_days}d</td>
            <td className={`px-3 py-2.5 text-xs font-mono ${x.is_overdue ? "text-red-600 font-semibold" : ""}`}>{fmtD(x.target_date)}</td>
            <td className="px-3 py-2.5"><DefPill s={x.status} /></td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export const RequirementsList = () => {
  const nav = useNavigate();
  const [d] = useList("/control-evidence-requirements");
  return (
    <Box testId="requirements-table">
      <Head cols={["Control", "Evidence requirement", "Type", "Mandatory", "Frequency", "Validity", "Submissions", "Coverage"]} />
      <tbody>
        {!d?.requirements?.length && <Empty cols={8} text="No evidence requirements defined." />}
        {d?.requirements?.map((r) => (
          <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" onClick={() => nav(`/controls/${r.control_id}?tab=evidence`)} data-testid={`req-row-${r.id}`}>
            <td className="px-3 py-2.5"><span className="font-mono text-[11px] text-slate-500">{r.control_ref}</span> {r.control_name}</td>
            <td className="px-3 py-2.5 text-[#003366]">{r.evidence_name}</td>
            <td className="px-3 py-2.5 text-xs">{r.type_label}</td>
            <td className="px-3 py-2.5 text-xs">{r.mandatory ? "Yes" : "No"}</td>
            <td className="px-3 py-2.5 text-xs">{lbl(r.frequency)}</td>
            <td className="px-3 py-2.5 text-xs">{r.validity_months ? `${r.validity_months} mo` : "-"}</td>
            <td className="px-3 py-2.5 text-[11px] font-mono">{Object.entries(r.submissions || {}).map(([k, v]) => `${lbl(k)} ${v}`).join(" · ") || "-"}</td>
            <td className="px-3 py-2.5"><EvidPill s={r.coverage === "ACCEPTED" ? "COMPLETE" : r.coverage} /></td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export const EvidenceTable = ({ rows, onReview, canReview, testId = "evidence-table" }) => (
  <Box testId={testId}>
    <Head cols={["File", "Control / target", "Requirement", "Period", "Submitted", "SHA-256", "Scan", "Status", ""]} />
    <tbody>
      {!rows?.length && <Empty cols={9} text="No evidence submitted." />}
      {rows?.map((e) => (
        <tr key={e.id} className="border-b border-slate-50" data-testid={`evidence-row-${e.id}`}>
          <td className="px-3 py-2.5 text-xs"><div className="font-medium text-[#003366] break-all">{e.file?.file_name}</div><div className="font-mono text-slate-400">{e.file?.reference_number} · {e.file?.classification}</div></td>
          <td className="px-3 py-2.5 text-[11px]">{e.control_ref && <div className="font-mono">{e.control_ref}</div>}{lbl(e.target_type)}</td>
          <td className="px-3 py-2.5 text-xs">{e.requirement_name || "-"}</td>
          <td className="px-3 py-2.5 text-[11px] font-mono">{e.evidence_period_start ? `${fmtD(e.evidence_period_start)} - ${fmtD(e.evidence_period_end)}` : "-"}</td>
          <td className="px-3 py-2.5 text-[11px]">{e.submitted_by}<div className="text-slate-400">{fmtDT(e.submitted_at)}</div>{e.reviewed_by && <div className="text-slate-400">Reviewed: {e.reviewed_by}</div>}{e.rejection_reason && <div className="text-red-600">{e.rejection_reason}</div>}</td>
          <td className="px-3 py-2.5 text-[10px] font-mono text-slate-500">{e.file?.sha256?.slice(0, 12)}…</td>
          <td className="px-3 py-2.5 text-[10px]">{lbl(e.file?.malware_scan_status)}</td>
          <td className="px-3 py-2.5"><EvidPill s={e.acceptance_status} testId={`evidence-status-${e.id}`} />{e.expires_at && <div className="text-[10px] text-slate-400">exp {fmtD(e.expires_at)}</div>}</td>
          <td className="px-3 py-2.5 whitespace-nowrap">
            {!e.restricted_hidden && <Button size="sm" variant="ghost" onClick={() => run(() => downloadControlEvidence(e.id))} data-testid={`evidence-download-${e.id}`}><Download className="w-4 h-4" /></Button>}
            {canReview && e.acceptance_status === "SUBMITTED" && <>
              <Button size="sm" variant="outline" className="mr-1" onClick={() => onReview(e, "ACCEPTED")} data-testid={`evidence-accept-${e.id}`}>Accept</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => onReview(e, "REJECTED")} data-testid={`evidence-reject-${e.id}`}>Reject</Button></>}
          </td>
        </tr>
      ))}
    </tbody>
  </Box>
);

export const reviewEvidence = async (e, decision, after) => {
  const reason = decision === "REJECTED" ? window.prompt("Rejection reason") : "";
  if (decision === "REJECTED" && !reason) return;
  await run(() => api.post(`/control-evidence/${e.id}/review`, { decision, reason }), decision === "ACCEPTED" ? "Evidence accepted" : "Evidence rejected");
  after?.();
};

export const EvidenceRepo = ({ meta }) => {
  const [status, setStatus] = useState("");
  const [d, load] = useList("/control-evidence", status ? { status } : {});
  return (
    <div className="space-y-3" data-testid="evidence-repository">
      <div className="flex gap-2 items-center">
        <div className="w-48"><Pick value={status || NONE} none="All statuses" options={opts(meta?.evidence_states)} onChange={(v) => setStatus(v === NONE ? "" : v)} testId="evidence-status-filter" /></div>
        <span className="text-xs text-slate-500">{Object.entries(d?.summary || {}).map(([k, v]) => `${lbl(k)}: ${v}`).join(" · ")}</span>
      </div>
      <EvidenceTable rows={d?.evidence} canReview={hasPerm(meta?.my_permissions, "CONTROL_EVIDENCE_APPROVE")} onReview={(e, dec) => reviewEvidence(e, dec, load)} testId="evidence-repo-table" />
    </div>
  );
};

export const ReportsPanel = ({ meta }) => {
  const [hist, loadHist] = useList("/control-reports/history");
  const [kind, setKind] = useState("ropa_activity");
  const [cov] = useList("/control-reports/coverage", { kind });
  const can = hasPerm(meta?.my_permissions, "CONTROL_EXPORT");
  const go = (rt, fmt) => run(() => exportReport(rt, fmt), `${lbl(fmt).toUpperCase()} generated`).then(loadHist);
  return (
    <div className="space-y-4" data-testid="reports-panel">
      <Card title="Reports and exports" action={<span className="text-[11px] text-slate-500">Every export records template version, timestamp, generator, filters, record count and SHA-256.</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {opts(meta?.report_types).map(([k, l]) => (
            <div key={k} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2" data-testid={`report-${k}`}>
              <span className="text-sm text-[#003366]">{l}</span>
              {can && <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => go(k, "csv")} data-testid={`report-${k}-csv`}>CSV</Button>
                <Button size="sm" variant="outline" onClick={() => go(k, "xlsx")} data-testid={`report-${k}-xlsx`}><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />XLSX</Button>
                <Button size="sm" variant="outline" onClick={() => go(k, "pdf")} data-testid={`report-${k}-pdf`}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
              </div>}
            </div>
          ))}
        </div>
      </Card>
      <Card title="Coverage" action={<div className="w-52"><Pick value={kind} onChange={setKind} testId="coverage-kind" options={[["ropa_activity", "RoPA activities"], ["ropa_asset", "RoPA assets"], ["vendor", "Vendors"], ["retention_rule", "Retention rules"], ["app_a_finding", "App A findings (mapped)"]]} /></div>}>
        <div className="text-xs text-slate-500 mb-2" data-testid="coverage-summary">{cov?.covered ?? 0} of {cov?.total ?? 0} covered by at least one control</div>
        <div className="max-h-72 overflow-y-auto"><table className="w-full text-sm"><tbody>
          {cov?.rows?.map((r) => <tr key={r.id} className="border-b border-slate-50"><td className="py-1.5 text-[#003366]">{r.name}</td><td className="text-xs font-mono">{r.control_refs || <span className="text-red-600">No control</span>}</td><td className="text-xs text-right">{r.effective_controls}/{r.controls} effective</td></tr>)}
        </tbody></table></div>
      </Card>
      <Card title="Generated reports">
        <table className="w-full text-xs" data-testid="report-history"><tbody>
          {hist?.reports?.slice(0, 15).map((r) => <tr key={r.id} className="border-b border-slate-50"><td className="py-1.5">{meta?.report_types?.[r.report_type]}</td><td className="uppercase">{r.format}</td><td>v{r.template_version}</td><td>{r.record_count} rows</td><td>{r.generated_by}</td><td>{fmtDT(r.generated_at)}</td><td className="font-mono text-slate-400">{r.sha256.slice(0, 16)}…</td></tr>)}
        </tbody></table>
      </Card>
    </div>
  );
};
